/*eslint-disable*/
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

// @mui/material components
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TablePagination from '@mui/material/TablePagination';
import { makeStyles } from '@mui/styles';

// @mui/icons-material
import Edit from '@mui/icons-material/Edit';
import Search from '@mui/icons-material/Search';
import Store from '@mui/icons-material/Store';

// core components
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Table from 'components/Table/Table.js';

// styles
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

// Redux
import { useLazySearchLocationsQuery } from 'api/location.api';
import { useGetReferenceListsQuery } from 'api/reference-list.api';

const useStyles = makeStyles(styles);

export default function SearchCriteria(props) {
  const classes = useStyles();
  const { data: referenceLists = [] } = useGetReferenceListsQuery();
  const { addLocation, tenantStatus } = props;

  // State
  const [dirtyData, setDirtyData] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locationId, setLocationId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [status, setStatus] = useState('');

  // API
  const [searchLocations, { data: searchResults, isFetching }] =
    useLazySearchLocationsQuery();

  // Search function
  const searchForLocations = () => {
    const searchCriteria = {
      page: page + 1, // API uses 1-based indexing
      rowsPerPage: rowsPerPage,
    };

    if (locationId) searchCriteria.location_id = locationId;
    if (locationName) searchCriteria.location_name = locationName;
    if (status) searchCriteria.status = status;

    searchLocations(searchCriteria);
    setDirtyData(false);
  };

  // Validation - allow search with no criteria to show all locations
  const validCriteria = () => {
    return true; // Always allow search to enable pagination and "show all"
  };

  // Load all locations on mount
  useEffect(() => {
    searchLocations({
      page: 1,
      rowsPerPage: 10,
    });
  }, []);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);

    // Trigger search with new page
    const searchCriteria = {
      page: newPage + 1,
      rowsPerPage: rowsPerPage,
    };
    if (locationId) searchCriteria.location_id = locationId;
    if (locationName) searchCriteria.location_name = locationName;
    if (status) searchCriteria.status = status;
    searchLocations(searchCriteria);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    // Trigger search with new rows per page
    const searchCriteria = {
      page: 1,
      rowsPerPage: newRowsPerPage,
    };
    if (locationId) searchCriteria.location_id = locationId;
    if (locationName) searchCriteria.location_name = locationName;
    if (status) searchCriteria.status = status;
    searchLocations(searchCriteria);
  };

  // Build table data
  const buildTableData = () => {
    if (!searchResults || !searchResults.results) return [];

    return searchResults.results.map((location) => {
      const editButton = (
        <div className='actions-left'>
          <Button
            justIcon
            round
            simple
            onClick={() => props.editLocation(location)}
            color='warning'
            className='edit'
          >
            <Edit />
          </Button>
        </div>
      );

      return [
        location.key,
        location.name,
        location.status,
        editButton,
      ];
    });
  };

  return (
    <>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <Store />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>Search Locations</h4>
              <Button
                color='rose'
                onClick={addLocation}
                className={classes.registerButton}
                disabled={tenantStatus !== 'active'}
              >
                + Add Location
              </Button>
            </CardHeader>
            <CardBody>
              {tenantStatus !== 'active' && (
                <p className={classes.textDanger}>
                  Please Activate Environment to add locations.
                </p>
              )}
              <form>
                <GridContainer>
                  <GridItem xs={12} sm={6} md={3}>
                    <CustomInput
                      labelText="Location ID"
                      id="location-id"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        type: 'text',
                        name: 'location-id-search',
                        'data-lpignore': 'true',
                        'data-form-type': 'other',
                        'data-1p-ignore': 'true',
                        value: locationId,
                        onChange: (e) => {
                          setLocationId(e.target.value);
                          setDirtyData(true);
                        },
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={6} md={3}>
                    <CustomInput
                      labelText="Location Name"
                      id="location-name"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        type: 'text',
                        name: 'location-name-search',
                        'data-lpignore': 'true',
                        'data-form-type': 'other',
                        'data-1p-ignore': 'true',
                        value: locationName,
                        onChange: (e) => {
                          setLocationName(e.target.value);
                          setDirtyData(true);
                        },
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={6} md={3}>
                    <FormControl
                      variant='standard'
                      className={classes.formControl}
                      fullWidth={true}
                      style={{ marginTop: '6px' }}
                    >
                      <InputLabel
                        id="status-select-label"
                        className={classes.inputLabel}
                      >
                        Status
                      </InputLabel>
                      <Select
                        variant='standard'
                        className={classes.select}
                        labelId="status-select-label"
                        id="status-select"
                        value={status}
                        onChange={(e) => {
                          setStatus(e.target.value);
                          setDirtyData(true);
                        }}
                      >
                        <MenuItem value="">
                          All
                        </MenuItem>
                        <MenuItem value="active">
                          Active
                        </MenuItem>
                        <MenuItem value="inactive">
                          Inactive
                        </MenuItem>
                        <MenuItem value="pending">
                          Pending
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </GridItem>
                </GridContainer>
                <GridContainer justify="flex-end">
                  <GridItem>
                    <Button
                      color="primary"
                      onClick={searchForLocations}
                      disabled={!dirtyData || !validCriteria()}
                    >
                      <Search /> Search
                    </Button>
                  </GridItem>
                </GridContainer>
              </form>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {searchResults && (
        <GridContainer>
          <GridItem xs={12}>
            <Card>
              <CardHeader color="primary" icon>
                <CardIcon color="primary">
                  <Store />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>
                  Search Results
                </h4>
              </CardHeader>
              <CardBody>
                <Table
                  tableHead={[
                    'Location ID',
                    'Location Name',
                    'Status',
                    'Actions',
                  ]}
                  tableData={buildTableData()}
                  customCellClasses={[
                    classes.left,
                    classes.left,
                    classes.left,
                    classes.right,
                  ]}
                  customClassesForCells={[0, 1, 2, 3]}
                  customHeadCellClasses={[
                    classes.left,
                    classes.left,
                    classes.left,
                    classes.right,
                  ]}
                  customHeadClassesForCells={[0, 1, 2, 3]}
                />
                <TablePagination
                  component="div"
                  count={searchResults.total || 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      )}
    </>
  );
}
